import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { CornerBadge, Position } from '@sorare/core/src/components/CornerBadge';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { cart } from '@sorare/core/src/lib/glossary';

import { getSteppedPrimaryOffer } from '#src/hooks/primaryBuy/steppedPrimaryOffer.ts';

import type { PriceLockedRibbon_primaryOffer } from './__generated__/index.graphql.ts';

const Anchor = styled.div`
  position: absolute;
  top: var(--ribbon-top, var(--unit));
  left: var(--ribbon-left, auto);
  right: var(--ribbon-right, auto);
  transform: var(--ribbon-transform, none);
  z-index: 2;
`;

type Props = {
  primaryOffer: PriceLockedRibbon_primaryOffer;
  // 'inline' leaves the positioning to a parent already stacking corner badges.
  position?: 'top-left' | 'top-right' | 'inline';
  // Center on the parent's top border (over blocks/rows) vs. inset inside it (over card art).
  straddle?: boolean;
};

const ANCHOR_STYLES: Record<
  'top-left' | 'top-right',
  Record<string, string>
> = {
  'top-left': { '--ribbon-left': '0' },
  'top-right': { '--ribbon-right': '0' },
};

const STRADDLE_STYLE = {
  '--ribbon-top': '0',
  '--ribbon-transform': 'translateY(-50%)',
};

export const PriceLockedRibbon = ({
  primaryOffer,
  position = 'top-right',
  straddle = false,
}: Props) => {
  const { isCampaign, soldOut, endDate, color } =
    getSteppedPrimaryOffer(primaryOffer);

  if (!isCampaign) return null;

  const badge = (
    /* Same supply band color as the stock counter's gauge and text. */
    <CornerBadge
      align={position === 'top-left' ? Position.LEFT : Position.RIGHT}
      color={color}
    >
      {soldOut ? (
        <BrandXS color="var(--c-black)">
          <FormattedMessage {...cart.soldOut} />
        </BrandXS>
      ) : (
        <>
          <FontAwesomeIcon icon={iconLock} color="var(--c-black)" size="xs" />
          <BrandXS color="var(--c-black)">
            {!!endDate && <TimeLeft time={endDate} />}
          </BrandXS>
        </>
      )}
    </CornerBadge>
  );

  if (position === 'inline') return badge;

  return (
    <Anchor
      style={
        {
          ...ANCHOR_STYLES[position],
          ...(straddle ? STRADDLE_STYLE : {}),
        } as CSSProperties
      }
    >
      {badge}
    </Anchor>
  );
};

PriceLockedRibbon.fragments = {
  primaryOffer: gql`
    fragment PriceLockedRibbon_primaryOffer on TokenPrimaryOffer {
      id
      ...SteppedPrimaryOffer_primaryOffer
    }
    ${getSteppedPrimaryOffer.fragments.primaryOffer}
  ` as TypedDocumentNode<PriceLockedRibbon_primaryOffer>,
};
